import { Mail, Phone, MapPin, Send, Clock, MessageSquare, User, Building2, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      content: "contato@coddar.com.br",
      link: "mailto:contato@coddar.com.br",
      color: "from-blue-500/20 to-blue-600/20"
    },
    {
      icon: Phone,
      title: "WhatsApp",
      content: "+55 (13) 99704-9254",
      link: "https://wa.me/5513997049254?text=Olá!%20Vim%20pelo%20site%20e%20gostaria%20de%20conversar.",
      color: "from-green-500/20 to-green-600/20"
    },
    {
      icon: MapPin,
      title: "Localização",
      content: "Santos, SP - Brasil",
      link: null,
      color: "from-purple-500/20 to-purple-600/20"
    },
    {
      icon: Clock,
      title: "Horário",
      content: "Seg - Sex: 9h às 17h",
      link: null,
      color: "from-orange-500/20 to-orange-600/20"
    }
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);

    // Adicionar access key do Web3Forms
    const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

    if (!accessKey) {
      toast({
        title: "Configuração pendente",
        description: "Por favor, configure a chave de acesso do Web3Forms.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    formData.append("access_key", accessKey);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Mensagem enviada com sucesso!",
          description: "Entraremos em contato em breve.",
        });
        (e.target as HTMLFormElement).reset();
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      toast({
        title: "Erro ao enviar mensagem",
        description: "Por favor, tente novamente ou entre em contato pelo WhatsApp.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl animate-glow" />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-primary font-medium transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-24 md:py-36 px-4 relative">
        <div className="container mx-auto text-center relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-16">
            <ArrowRight className="h-4 w-4 rotate-180" />
            Voltar para Home
          </Link>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8 backdrop-blur-sm animate-fade-in">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Estamos aqui para ajudar</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-8 max-w-4xl mx-auto leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Entre em{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
              contato conosco
            </span>
          </h1>

          <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Tem um projeto em mente? Quer saber mais sobre nossos serviços? Preencha o formulário abaixo e nossa equipe entrará em contato.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              const Wrapper = info.link ? 'a' : 'div';
              const wrapperProps = info.link ? { href: info.link } : {};

              return (
                <Wrapper key={index} {...wrapperProps} className={info.link ? "block" : ""}>
                  <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:scale-105 group h-full">
                    <CardHeader className="text-center">
                      <div className={`h-16 w-16 rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform relative`}>
                        <Icon className="h-8 w-8 text-primary relative z-10" />
                        <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <CardTitle className="text-lg text-foreground">{info.title}</CardTitle>
                      <CardDescription className="text-sm text-muted-foreground">
                        {info.content}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-4 relative">
        <div className="container mx-auto relative z-10 max-w-4xl">
          <Card className="border-border/50 bg-card/50 backdrop-blur-xl">
            <CardHeader className="text-center">
              <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-3xl text-foreground">Envie sua mensagem</CardTitle>
              <CardDescription className="text-base">
                Preencha o formulário abaixo e retornaremos o mais breve possível
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Nome */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" />
                      Nome completo
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Seu nome"
                      required
                      className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="seu@email.com"
                      required
                      className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Telefone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-primary" />
                      Telefone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(13) 99999-9999"
                      className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                    />
                  </div>

                  {/* Empresa */}
                  <div className="space-y-2">
                    <Label htmlFor="company" className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" />
                      Empresa
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      placeholder="Nome da empresa"
                      className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                {/* Assunto */}
                <div className="space-y-2">
                  <Label htmlFor="subject">Assunto</Label>
                  <Select name="subject" required>
                    <SelectTrigger className="bg-background/50 border-border/50 focus:border-primary transition-colors">
                      <SelectValue placeholder="Selecione o assunto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desenvolvimento">Desenvolvimento de Software</SelectItem>
                      <SelectItem value="automacao">Automação de Processos</SelectItem>
                      <SelectItem value="seguranca">Segurança Digital</SelectItem>
                      <SelectItem value="consultoria">Consultoria em TI</SelectItem>
                      <SelectItem value="orcamento">Solicitar Orçamento</SelectItem>
                      <SelectItem value="suporte">Suporte Técnico</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Mensagem */}
                <div className="space-y-2">
                  <Label htmlFor="message">Mensagem</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Conte-nos mais sobre seu projeto ou dúvida..."
                    rows={6}
                    required
                    className="bg-background/50 border-border/50 focus:border-primary transition-colors resize-none"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-4">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="gap-2 group relative overflow-hidden min-w-[200px]"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {isSubmitting ? (
                        <>
                          <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar mensagem
                          <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-4 relative">
        <div className="container mx-auto relative z-10 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-lg text-muted-foreground">
              Respostas rápidas para as dúvidas mais comuns
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Qual o prazo de resposta?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Respondemos todas as mensagens em até 24 horas úteis.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Fazem orçamento gratuito?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Sim! Todos os orçamentos são gratuitos e sem compromisso.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Atendem todo o Brasil?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Sim, atendemos clientes em todo território nacional de forma remota.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Oferecem suporte pós-projeto?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Sim, oferecemos suporte e manutenção para todos os projetos desenvolvidos.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10 max-w-4xl">
          <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            <CardHeader className="text-center pb-6 relative z-10">
              <CardTitle className="text-3xl md:text-4xl mb-4 text-foreground">
                Prefere falar pelo WhatsApp?
              </CardTitle>
              <CardDescription className="text-foreground/80 text-lg">
                Nossa equipe está pronta para atender você
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center relative z-10">
              <Button size="lg" asChild className="gap-2 group/btn bg-green-600 hover:bg-green-700">
                <a href="https://wa.me/5513997049254?text=Olá!%20Vim%20pelo%20site%20e%20gostaria%20de%20conversar." target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  +55 (13) 99704-9254
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Contact;
